import { tb } from '#/store/translation/translation.store';

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30.4369 * DAY;
const YEAR = 365.2425 * DAY;

const formatterPattern = /^(\w+)\((.*)\)$/;

/**
 * Formats a date value to a relative date format.
 * @param value ISO date string ("2023-12-31T00:00:00Z"), or number of seconds since Unix epoch (1703980800).
 * @returns Formatted relative date ("8 months ago").
 * @see https://github.com/Accedo-Products/one-dynamic-ui-specification/blob/master/docs/i18next-string-formatting.md#algorithm
 */
const formatDateRelative = (value: string | number): string => {
  const targetDate =
    typeof value === 'number' ? new Date(value * 1000) : new Date(value);

  const targetSec = Number(targetDate) / 1000;
  const nowSec = Date.now() / 1000;

  const absDifferenceSec = Math.abs(nowSec - targetSec);
  const futureOrPast = nowSec < targetSec ? 'future' : 'past';

  let unitString = '';
  let unitTimeSec = 0;

  if (absDifferenceSec < HOUR) {
    unitString = 'minute';
    unitTimeSec = MINUTE;
  } else if (absDifferenceSec < DAY) {
    unitString = 'hour';
    unitTimeSec = HOUR;
  } else if (absDifferenceSec < 7 * DAY) {
    unitString = 'day';
    unitTimeSec = DAY;
  } else if (absDifferenceSec < YEAR) {
    unitString = 'month';
    unitTimeSec = MONTH;
  } else {
    unitString = 'year';
    unitTimeSec = YEAR;
  }

  const unitAmount = Math.max(Math.floor(absDifferenceSec / unitTimeSec), 1);
  const key = `relativeTimeUnit.${unitString}.${futureOrPast}`;

  return tb(key, { count: unitAmount, myDate: targetDate });
};

/**
 * Formats a date value, according to a Unix-style strftime format string.
 * @param value ISO date string ("2023-12-31T00:00:00Z").
 * @param format Unix-style strftime format string ("%e %b, %Y").
 * @param locale Current locale passed down from i18next.
 * @returns Formatted date ("31 Dec, 2023").
 * @see https://strftime.net
 */
const formatDateStrf = (
  value: string,
  format: string,
  locale: string
): string =>
  // convert the strftime format to LDML, so we can reuse our localized LDML formatter
  formatDateLdml(value, strfToLdml(format), locale);

/**
 * Formats a date value, according to an LDML format string.
 * Uses Intl instead of Day.js to localize month and weekday names without importing all the Day.js locales.
 * @param value ISO date string ("2023-12-31T00:00:00Z").
 * @param format LDML format string ("d LLL, YYYY"), or more specifically Moment/Day.js format.
 * @param locale Current locale passed down from i18next.
 * @returns Formatted date ("31 Dec, 2023").
 * @see https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * @see https://day.js.org/docs/en/display/format
 */
const formatDateLdml = (
  value: string,
  format: string,
  locale: string
): string => {
  const date = new Date(value);
  const timeZone = undefined;

  const pad = (num: number, min = 2) =>
    new Intl.NumberFormat(locale, {
      minimumIntegerDigits: min,
      useGrouping: false,
    }).format(num);

  // get numbers from the date
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = date.getDay();
  const hour = date.getHours();
  const hour12 = hour % 12 || 12;
  const minute = date.getMinutes();
  const second = date.getSeconds();

  // get names from Intl
  const getMonthName = (width: 'long' | 'short') =>
    new Intl.DateTimeFormat(locale, { month: width, timeZone }).format(date);

  const getWeekdayName = (width: 'long' | 'short') =>
    new Intl.DateTimeFormat(locale, { weekday: width, timeZone }).format(date);

  const getPeriodName = () =>
    new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true, timeZone })
      .formatToParts(date)
      .find((part) => part.type === 'dayPeriod')?.value ?? '';

  // token replacement
  return format.replace(
    /YYYY|YY|MMMM|MMM|MM|M|dddd|ddd|d|DD|D|HH|H|hh|h|mm|m|ss|s|A|a/g,
    (token) => {
      switch (token) {
        case 'YYYY':
          return String(year);
        case 'YY':
          return pad(year % 100);
        case 'MMMM':
          return getMonthName('long');
        case 'MMM':
          return getMonthName('short');
        case 'MM':
          return pad(month);
        case 'M':
          return String(month);
        case 'dddd':
          return getWeekdayName('long');
        case 'ddd':
          return getWeekdayName('short');
        case 'd':
          return String(dayOfWeek);
        case 'DD':
          return pad(day);
        case 'D':
          return String(day);
        case 'HH':
          return pad(hour);
        case 'H':
          return String(hour);
        case 'hh':
          return pad(hour12);
        case 'h':
          return String(hour12);
        case 'mm':
          return pad(minute);
        case 'm':
          return String(minute);
        case 'ss':
          return pad(second);
        case 's':
          return String(second);
        case 'A':
          return getPeriodName().toUpperCase();
        case 'a':
          return getPeriodName().toLowerCase();
        default:
          return token;
      }
    }
  );
};

/**
 * Formats a number of seconds to duration format (H:MM:SS). Hours are omitted if zero.
 * Numbers are individually padded, but leading zeroes are omitted from the result.
 * (0:01, 1:02, 12:03, 1:02:03, 123:45:56).
 * @param value Number of seconds (5025).
 * @returns Formatted duration ("1:23:45").
 * @see https://github.com/Accedo-Products/one-dynamic-ui-specification/blob/master/docs/i18next-string-formatting.md#duration-time
 */
const formatNumberDuration = (value: number): string => {
  const hour = value / HOUR;
  const min = (value % HOUR) / MINUTE;
  const sec = value % MINUTE;

  return [hour, min, sec]
    .map((x) => String(Math.floor(x)).padStart(2, '0'))
    .join(':')
    .replace(/^(00:0|00:|0)/, '');
};

/**
 * Converts data bound values inside translations into different formats.
 * @param value The data bound value we have to format.
 * @param formatter Describes how to format the value. It can be:
 * - New convention: The name of the formatter and an optional format parameter in a function call-looking syntax, ex: "datetime(%e %b, %Y)" or "relativetime()".
 * - Old convention: Only an LDML format string, ex: "YYYY/MM/DD".
 * @param [locale] Current locale passed down from i18next, defaults to English.
 * @returns The formatted string value.
 * @see https://github.com/Accedo-Products/one-dynamic-ui-specification/blob/master/docs/i18next-string-formatting.md
 * @see https://www.i18next.com/translation-function/formatting
 */
export const i18nextFormatter = (
  value: string | number | Date | undefined,
  formatter = '',
  locale = 'en'
) => {
  // the data bound value is missing
  if (value === undefined) {
    return '';
  }

  // convert date to string
  if (value instanceof Date) {
    value = value.toISOString();
  }

  // detect formatter name and optional format parameter
  const [, name, format] = formatterPattern.exec(formatter) ?? [];

  // strftime date with format parameter, ex: "__item.releaseDate, datetime(%e %b, %Y)__" -> "31 Dec, 2023"
  if (name === 'datetime' && typeof value === 'string') {
    return formatDateStrf(value, format, locale);
  }

  // relative date, no format parameter, ex: "__item.releaseDateEpoch, relativetime()__" -> "10d ago"
  if (name === 'relativetime') {
    return formatDateRelative(value);
  }

  // duration, no format parameter, ex: "__item.video.duration, duration()__" -> "1:23:45"
  if (name === 'duration' && typeof value === 'number') {
    return formatNumberDuration(value);
  }

  // fall back to the old format, ex: "__item.releaseDate, YYYY/MM/DD__" -> "2023/12/31"
  if (typeof value === 'string' && !name) {
    return formatDateLdml(value, formatter, locale);
  }

  // we can't even do the fallback
  return '';
};

// converts a STRF format string to LDML
const strfToLdml = (format: string) =>
  format.replace(
    /%\w/g,
    (token) =>
      ({
        '%Y': 'YYYY',
        '%y': 'YY',
        '%m': 'MM',
        '%b': 'MMM',
        '%B': 'MMMM',
        '%a': 'ddd',
        '%A': 'dddd',
        '%w': 'd',
        '%d': 'DD',
        '%e': 'D',
        '%H': 'HH',
        '%I': 'hh',
        '%k': 'H',
        '%l': 'h',
        '%M': 'mm',
        '%S': 'ss',
        '%p': 'A',
        '%P': 'a',
      })[token] ?? token
  );
